import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useCallback
} from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

interface SidebarState {
  collapsed: boolean;
  mobileOpen: boolean;
  width: { collapsed: number; expanded: number };
}

type SidebarAction =
  | { type: 'TOGGLE_COLLAPSE' }
  | { type: 'SET_COLLAPSE'; payload: boolean }
  | { type: 'TOGGLE_MOBILE' }
  | { type: 'SET_MOBILE_OPEN'; payload: boolean }
  | { type: 'UPDATE_WIDTH'; payload: { collapsed: number; expanded: number } };

const sidebarReducer = (
  state: SidebarState,
  action: SidebarAction
): SidebarState => {
  switch (action.type) {
    case 'TOGGLE_COLLAPSE':
      return { ...state, collapsed: !state.collapsed };
    case 'SET_COLLAPSE':
      return { ...state, collapsed: action.payload };
    case 'TOGGLE_MOBILE':
      return { ...state, mobileOpen: !state.mobileOpen };
    case 'SET_MOBILE_OPEN':
      return { ...state, mobileOpen: action.payload };
    case 'UPDATE_WIDTH':
      return { ...state, width: action.payload };
    default:
      return state;
  }
};

interface SidebarContextType extends SidebarState {
  toggleCollapse(): void;
  setCollapsed(v: boolean): void;
  toggleMobile(): void;
  setMobileOpen(v: boolean): void;
  closeMobile(): void; // New helper function
  getWidth(): number;
  isMobile: boolean; // Expose mobile state
}

const SidebarContext = createContext<SidebarContextType | null>(null);

export const SidebarProvider: React.FC<{
  children: ReactNode;
  widthConfig?: { collapsed: number; expanded: number };
  initialCollapsed?: boolean;
}> = ({
  children,
  widthConfig = { collapsed: 72, expanded: 280 },
  initialCollapsed = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [state, dispatch] = useReducer(sidebarReducer, {
    collapsed: initialCollapsed,
    mobileOpen: false,
    width: widthConfig
  });

  // Update width config when it changes
  useEffect(() => {
    dispatch({ type: 'UPDATE_WIDTH', payload: widthConfig });
  }, [widthConfig]);

  // Handle mobile behavior
  useEffect(() => {
    if (isMobile) {
      // On mobile, always uncollapse and close mobile drawer when switching to desktop
      dispatch({ type: 'SET_COLLAPSE', payload: false });
    } else {
      // Close mobile drawer when switching to desktop
      dispatch({ type: 'SET_MOBILE_OPEN', payload: false });
    }
  }, [isMobile]);

  const toggleCollapse = useCallback(() => {
    if (!isMobile) {
      dispatch({ type: 'TOGGLE_COLLAPSE' });
    }
  }, [isMobile]);

  const setCollapsed = useCallback(
    (v: boolean) => {
      if (!isMobile) {
        dispatch({ type: 'SET_COLLAPSE', payload: v });
      }
    },
    [isMobile]
  );

  const toggleMobile = useCallback(() => {
    if (isMobile) {
      dispatch({ type: 'TOGGLE_MOBILE' });
    }
  }, [isMobile]);

  const setMobileOpen = useCallback(
    (v: boolean) => {
      if (isMobile) {
        dispatch({ type: 'SET_MOBILE_OPEN', payload: v });
      }
    },
    [isMobile]
  );

  const closeMobile = useCallback(() => {
    dispatch({ type: 'SET_MOBILE_OPEN', payload: false });
  }, []);

  const getWidth = useCallback(() => {
    // On mobile, when drawer is closed, return 0 width
    if (isMobile && !state.mobileOpen) {
      return 0;
    }
    // Otherwise return collapsed or expanded width
    return state.collapsed ? state.width.collapsed : state.width.expanded;
  }, [state, isMobile]);

  return (
    <SidebarContext.Provider
      value={{
        ...state,
        toggleCollapse,
        setCollapsed,
        toggleMobile,
        setMobileOpen,
        closeMobile,
        getWidth,
        isMobile
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
};